import { useAuth } from "@/context/auth";
import { useMemo, useState } from "react";
import { HomeRole, HomeSection, MAX_SHORTCUTS, defaultShortcuts, shortcutFallback } from "./sections";

const storageKey = (phone: string) => `home-shortcuts:${phone}`;

function readSaved(key: string) {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) || "null");
    return Array.isArray(value) && value.every((id) => typeof id === "string") ? (value as string[]) : null;
  } catch {
    return null;
  }
}

// Shortcuts are chosen per signed-in phone on this device. Stylists see every section they
// have, so only managers keep a customised list.
export default function useHomeShortcuts(role: HomeRole, available: HomeSection[]) {
  const phone = useAuth().getUserByToken()?.phoneNumber ?? "";
  const key = storageKey(phone);
  const [saved, setSaved] = useState(() => (role === "manager" ? readSaved(key) : null));

  const shortcuts = useMemo(() => {
    const byId = new Map(available.map((section) => [section.id, section]));
    const pick = (ids: string[]) =>
      ids.map((id) => byId.get(id)).filter((section): section is HomeSection => Boolean(section));
    if (saved) return pick(saved).slice(0, MAX_SHORTCUTS);
    const defaults = pick(defaultShortcuts[role]);
    const fillers = pick(shortcutFallback[role]).filter((section) => !defaults.includes(section));
    return [...defaults, ...fillers].slice(0, MAX_SHORTCUTS);
  }, [available, role, saved]);

  const save = (ids: string[]) => {
    try {
      localStorage.setItem(key, JSON.stringify(ids));
    } catch {
      // Storage can be unavailable (private mode); the choice still applies until reload.
    }
    setSaved(ids);
  };

  const reset = () => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing stored.
    }
    setSaved(null);
  };

  return { shortcuts, save, reset, customized: saved !== null };
}
